import * as React from 'react';
import {
  Box,
  Icon,
  Spinner,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { statusIconMap as sharedStatusIconMap } from '../../../constants';
import type {
  StatusReportTableRowStatus,
  StatusReportTableRowStatusTextMap,
} from '../status-report-table-row.types';
import { getStatusReportCellCss } from './get-status-report-cell-css';

type ResolvedStatusTextParams = {
  status: StatusReportTableRowStatus;
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
  statusTextMap: StatusReportTableRowStatusTextMap;
};

const getResolvedStatusText = ({
  status,
  formatMessage,
  statusTextMap,
}: ResolvedStatusTextParams) => formatMessage({ id: statusTextMap[status] });

export type StatusReportTableRowStatusIconMap = Record<
  Exclude<StatusReportTableRowStatus, 'loading'>,
  {
    icon?: React.ComponentProps<typeof Icon>['icon'];
    tint?: React.ComponentProps<typeof Icon>['tint'];
  }
>;

/**
 * Renders the status table cell for the resolved status value.
 * Uses the status text map and optional icon map overrides.
 */
export const StatusReportStatusCell = ({
  value,
  statusTextMap,
  statusIconMap = sharedStatusIconMap,
}: {
  value: StatusReportTableRowStatus;
  statusTextMap: StatusReportTableRowStatusTextMap;
  statusIconMap?: StatusReportTableRowStatusIconMap;
}) => {
  const { formatMessage } = useIntl();
  const status = value;
  const resolvedStatusText = getResolvedStatusText({
    status,
    formatMessage,
    statusTextMap,
  });
  const isLoading = status === 'loading';
  const resolvedStatusIcon = isLoading ? undefined : statusIconMap[status];

  return (
    <Box as="td" css={getStatusReportCellCss('status')}>
      <StackLayout
        orientation="horizontal"
        spacing="$half"
        verticalAlignment="center"
        css={{ flexShrink: 0 }}
      >
        {status === 'loading' ? (
          <Spinner loading size="$one" renderLoadingText={false} />
        ) : (
          resolvedStatusIcon?.icon && (
            <Icon
              icon={resolvedStatusIcon.icon}
              size="$one"
              tint={resolvedStatusIcon.tint}
            />
          )
        )}
        <UtilityText
          as="span"
          size="base"
          emphasis={isLoading ? 'subtle' : undefined}
          css={{ whiteSpace: 'nowrap' }}
        >
          {resolvedStatusText}
        </UtilityText>
      </StackLayout>
    </Box>
  );
};
